import * as React from 'react';
import { ReactElement, useState } from 'react';
import merge from 'lodash/merge';
import { useForm, useFieldArray } from "react-hook-form";

import {
    Typography,
    FormControl,
    Box,
    styled,
    Button,
    FormHelperText
} from '@mui/material';

export const ArrayInput = (props: ArrayInputProps) => {
    const {
        className,
        defaultValue,
        value,
        label,
        children,
        disabled,
        helperText,
        error,
        margin = 'dense',
        name,
        onChange,
        ...rest
    } = props;

    const [data, setData] = useState(value ?? []);
    const { control} = useForm({
        mode: "onBlur",
        defaultValues: {
            [name]: value
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${name}`
    });

    const removeIndex = (index) => {
        remove(index);
        data.splice(index,1);
        setData(data);
        onChange(data, name);
    }

    const handleDataChange = (value, index: number, type: string) => {
        setData(merge(data, {[index]:{[type]: value }}));
        onChange(data, name);
    };

    return (
        <Root
            fullWidth
            margin={margin}
            className={className}
            {...rest}
        >

            <Typography component="p" variant="subtitle2">
                {label ?? name}
            </Typography>           
            {fields.map((field, index) => {
                return (
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap="0.5em"
                        sx={{
                            width: {
                                sm: "100%",
                                md: "100%",
                            },
                            paddingTop: "15px",
                            paddingBottom: "15px"
                        }}
                        key={field.id}
                    >
                        <>
                        {React.Children.map(children, child => {
                            return (
                                <Box display="flex" gap="0.5em">
                                {React.cloneElement(child, {
                                    key: `${index}_${child.props.name}`,
                                    value: data?.[index]?.[child.props.name] ?? "",
                                    onChange: (e) => handleDataChange(e, index, child.props.name)
                                })}
                                </Box>
                            )
                        })}
                        </>
                        <Button variant="outlined" color="error" onClick={() => removeIndex(index)}>
                            Remove
                        </Button>
                    </Box>
                );
            })}
            <Button 
                variant="contained" 
                onClick={() => append({})}
            >
                Add
            </Button>
            { helperText && <FormHelperText error={error}>{helperText}</FormHelperText> }
        </Root>
    );
};

export interface ArrayInputProps {
    formClassName?: string;
    fullWidth?: boolean;
    headerCellClassName?: string;
    margin?: 'none' | 'dense' | 'normal';
    variant?: 'standard' | 'outlined' | 'filled';
    className?: string;
    children: ReactElement;
    disabled?: boolean;
    label?: string;
    helperText?: string;
    error?: boolean;
    name: string;
    defaultValue?: any;
    value?: any;
    onChange?: any;
}

const PREFIX = 'CustomArrayInput';

export const ArrayInputClasses = {
    root: `${PREFIX}-root`,
    label: `${PREFIX}-label`,
};

const Root = styled(FormControl, {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
    marginTop: 0,
    [`& .${ArrayInputClasses.label}`]: {
        position: 'relative',
        top: theme.spacing(0.5),
        left: theme.spacing(-1.5),
    },
    [`& .${ArrayInputClasses.root}`]: {
        // nested ArrayInput
        paddingLeft: theme.spacing(2),
    },
}));
